import React, { useState, useEffect, useRef } from 'react';

// Custom styles for LanguageTranslator component
const styles = `
.language-translator {
    position: fixed;
    bottom: 30px;
    right: 30px;
    z-index: 9999;
}

/* Floating Button */
.translator-btn {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FF800D 0%, #e67300 100%);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 20px rgba(255, 128, 13, 0.4);
    transition: all 0.3s ease;
}

.translator-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 25px rgba(255, 128, 13, 0.5);
}

.translator-btn svg {
    width: 28px;
    height: 28px;
    fill: white;
}

/* Popup */
.translator-popup {
    position: absolute;
    bottom: 75px;
    right: 0;
    width: 280px;
    background-color: #FFFDF7;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(90, 52, 17, 0.15);
    overflow: hidden;
    opacity: 0;
    visibility: hidden;
    transform: translateY(20px);
    transition: all 0.3s ease;
}

.translator-popup.open {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
}

/* Popup Header */
.translator-header {
    background: linear-gradient(135deg, #2F4B20 0%, #3d6029 100%);
    padding: 18px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.translator-header-icon {
    width: 24px;
    height: 24px;
    fill: white;
}

.translator-header-text {
    font-family: 'Quicksand', sans-serif;
    font-size: 1.1rem;
    font-weight: 700;
    color: white;
    margin: 0;
}

/* Language List */
.translator-languages {
    padding: 10px 0;
    max-height: 320px;
    overflow-y: auto;
}

.translator-languages::-webkit-scrollbar {
    width: 6px;
}

.translator-languages::-webkit-scrollbar-track {
    background: #F5F1E9;
}

.translator-languages::-webkit-scrollbar-thumb {
    background: rgba(90, 52, 17, 0.3);
    border-radius: 3px;
}

.translator-languages::-webkit-scrollbar-thumb:hover {
    background: rgba(90, 52, 17, 0.5);
}

.language-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    background: transparent;
    width: 100%;
    text-align: left;
}

.language-item:hover {
    background-color: #F5F1E9;
}

.language-item.active {
    background-color: rgba(255, 128, 13, 0.1);
}

.language-flag {
    font-size: 1.4rem;
    line-height: 1;
}

.language-name {
    font-family: 'Quicksand', sans-serif;
    font-size: 0.95rem;
    font-weight: 600;
    color: #5A3411;
    margin: 0;
}

.language-item.active .language-name {
    color: #FF800D;
}

/* Checkmark for active language */
.language-check {
    margin-left: auto;
    width: 18px;
    height: 18px;
    fill: #FF800D;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.language-item.active .language-check {
    opacity: 1;
}

/* Powered by */
.translator-footer {
    padding: 12px 20px;
    background-color: #F5F1E9;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.translator-footer-text {
    font-family: 'Quicksand', sans-serif;
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(90, 52, 17, 0.6);
    margin: 0;
}

/* Responsive Styles */
@media (max-width: 575.98px) {
    .language-translator {
        bottom: 20px;
        right: 20px;
    }
    
    .translator-btn {
        width: 55px;
        height: 55px;
    }
    
    .translator-btn svg {
        width: 24px;
        height: 24px;
    }
    
    .translator-popup {
        width: 260px;
        right: -10px;
    }
    
    .translator-header {
        padding: 15px 18px;
    }
    
    .translator-header-text {
        font-size: 1rem;
    }
    
    .language-item {
        padding: 10px 18px;
    }
    
    .language-name {
        font-size: 0.9rem;
    }
}

@media (max-width: 400px) {
    .language-translator {
        bottom: 15px;
        right: 15px;
    }
    
    .translator-btn {
        width: 50px;
        height: 50px;
    }
    
    .translator-popup {
        width: 240px;
        bottom: 65px;
    }
}

/* Hide Google Translate default widget */
.goog-te-banner-frame,
.goog-te-balloon-frame,
#goog-gt-tt,
.goog-tooltip,
.goog-tooltip:hover {
    display: none !important;
}

body {
    top: 0 !important;
}

.goog-te-gadget {
    display: none !important;
}

.skiptranslate {
    display: none !important;
}

#google_translate_element {
    position: absolute;
    top: -9999px;
    left: -9999px;
}
`;

const LanguageTranslator = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const popupRef = useRef(null);
    const buttonRef = useRef(null);

    const languages = [
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'es', name: 'Español', flag: '🇪🇸' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'it', name: 'Italiano', flag: '🇮🇹' },
        { code: 'pt', name: 'Português', flag: '🇵🇹' },
        { code: 'pl', name: 'Polski', flag: '🇵🇱' },
        { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
        { code: 'ru', name: 'Русский', flag: '🇷🇺' },
        { code: 'zh-CN', name: '中文 (简体)', flag: '🇨🇳' },
        { code: 'ja', name: '日本語', flag: '🇯🇵' },
        { code: 'ko', name: '한국어', flag: '🇰🇷' },
        { code: 'ar', name: 'العربية', flag: '🇸🇦' },
        { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
        { code: 'ur', name: 'اردو', flag: '🇵🇰' },
        { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
    ];

    // Helper to thoroughly clear all googtrans cookies across paths/domains
    const clearGoogleTranslateCookies = () => {
        const hostname = window.location.hostname;
        const cookieNames = ['googtrans', 'googtrans_sys'];
        
        const domainParts = hostname.split('.');
        const isIpOrLocalhost = hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname) || hostname.endsWith('.local');
        
        const domains = ['']; // path-only cookie
        if (!isIpOrLocalhost) {
            domains.push(hostname);
            domains.push('.' + hostname);
            if (domainParts.length > 2) {
                const rootDomain = domainParts.slice(-2).join('.');
                domains.push(rootDomain);
                domains.push('.' + rootDomain);
            }
        }

        const paths = ['/', window.location.pathname];

        cookieNames.forEach(name => {
            domains.forEach(domain => {
                paths.forEach(path => {
                    const domainAttr = domain ? `; domain=${domain}` : '';
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domainAttr}`;
                });
            });
        });
    };

    // Check for existing translation cookie on mount
    useEffect(() => {
        const cookies = document.cookie.split(';');
        let foundLang = 'en';
        for (let cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === 'googtrans' && value) {
                const langCode = value.split('/').pop();
                if (langCode) {
                    foundLang = langCode;
                }
            }
        }
        setSelectedLanguage(foundLang);
    }, []);

    // Load Google Translate script
    useEffect(() => {
        // Check if already loaded
        if (window.google && window.google.translate) {
            return;
        }

        // Define the callback before loading script
        window.googleTranslateElementInit = function () {
            try {
                new window.google.translate.TranslateElement(
                    {
                        pageLanguage: 'en',
                        autoDisplay: false,
                        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE
                    },
                    'google_translate_element'
                );
            } catch (e) {
                console.log('Google Translate initialization skipped');
            }
        };

        // Add Google Translate script
        const existingScript = document.querySelector('script[src*="translate.google"]');
        if (!existingScript) {
            const script = document.createElement('script');
            script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
            script.async = true;
            script.onerror = () => {
                console.log('Google Translate script could not be loaded');
            };
            document.body.appendChild(script);
        }
    }, []);

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLanguageChange = (langCode) => {
        if (langCode === selectedLanguage) {
            setIsOpen(false);
            return;
        }

        setSelectedLanguage(langCode);
        setIsOpen(false);

        // First, clear any pre-existing translation cookies
        clearGoogleTranslateCookies();

        if (langCode !== 'en') {
            const cookieValue = `/en/${langCode}`;
            const hostname = window.location.hostname;
            const isIpOrLocalhost = hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname) || hostname.endsWith('.local');

            // Always set path-only cookie (works on localhost, IP, and standard hosts)
            document.cookie = `googtrans=${cookieValue}; path=/`;

            // If on a domain, also set domain scoped cookie for subdomains
            if (!isIpOrLocalhost) {
                document.cookie = `googtrans=${cookieValue}; path=/; domain=${hostname}`;
                document.cookie = `googtrans=${cookieValue}; path=/; domain=.${hostname}`;
                const domainParts = hostname.split('.');
                if (domainParts.length > 2) {
                    const rootDomain = domainParts.slice(-2).join('.');
                    document.cookie = `googtrans=${cookieValue}; path=/; domain=.${rootDomain}`;
                }
            }
        }

        // Reload the page to apply the clean translation state from English
        setTimeout(() => {
            window.location.reload();
        }, 50);
    };

    return (
        <>
            <style>{styles}</style>
            {/* Hidden Google Translate Element */}
            <div id="google_translate_element"></div>

            <div className="language-translator">
                {/* Floating Button */}
                <button
                    ref={buttonRef}
                    className="translator-btn"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label="Translate website"
                >
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" />
                    </svg>
                </button>

                {/* Popup */}
                <div
                    ref={popupRef}
                    className={`translator-popup ${isOpen ? 'open' : ''}`}
                >
                    {/* Header */}
                    <div className="translator-header">
                        <svg className="translator-header-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" />
                        </svg>
                        <p className="translator-header-text">Select Language</p>
                    </div>

                    {/* Language List */}
                    <div className="translator-languages">
                        {languages.map((lang) => (
                            <button
                                key={lang.code}
                                className={`language-item ${selectedLanguage === lang.code ? 'active' : ''}`}
                                onClick={() => handleLanguageChange(lang.code)}
                            >
                                <span className="language-flag">{lang.flag}</span>
                                <p className="language-name">{lang.name}</p>
                                <svg className="language-check" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                </svg>
                            </button>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="translator-footer">
                        <p className="translator-footer-text">Powered by Google Translate</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LanguageTranslator;
